import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AutoComplete, Button, Card, Col, Form, Input, Radio, Row, Typography } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import CollectionService from '../../service/CollectionService'
import RecentService from '../../service/RecentService'
import WallexService from '../../service/WallexService'
import { CollectionMethod, CollectionPayload } from '../../domain/Collection'
import { RecentOption, useRecentValues } from '../../hooks/useRecent'
import { collectionSample, fillEmpty } from '../../data/samples'
import { paths } from '../../routes'
import { groupThousands } from '../../util/format'
import AmountField from '../ui/AmountField'
import CreatedTicket from '../ui/CreatedTicket'
const { Text } = Typography

const channelOptions = [
    { value: CollectionMethod.LOCAL, label: 'Local' },
    { value: CollectionMethod.SWIFT, label: 'SWIFT' },
]

const matchesInput = (input: string, option?: { label?: React.ReactNode }) =>
    String(option?.label ?? '')
        .toLowerCase()
        .includes(input.toLowerCase())

const CollectionCreateForm: React.FC = () => {
    const [form] = Form.useForm<CollectionPayload>()
    const accountNumberOptions = useRecentValues('accountNumber')
    const navigate = useNavigate()
    const [created, setCreated] = useState<{ id: string; values: CollectionPayload } | null>(null)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const onFinish = (formValues: CollectionPayload) => {
        // The sender bank country comes from the BIC (characters 5-6), e.g. DBSSSGSG -> SG
        const bic = formValues.senderBankBIC || ''
        const values = { ...formValues, senderBankCountry: bic.length >= 6 ? bic.substring(4, 6).toUpperCase() : 'SG' }

        setSubmitting(true)
        setError(null)
        CollectionService.simulate(values)
            .then((result) => {
                RecentService.record({ kind: 'collection', id: result.id, label: values.customerReference || values.senderName })
                setCreated({ id: result.id, values })
            })
            .catch((e) => setError(WallexService.errorDescription(e, 'Couldn’t create the collection.')))
            .finally(() => setSubmitting(false))
    }

    // Picking a recent collection account also fills in its name
    const onPickAccount = (_: string, option: RecentOption) => {
        if (option.name && !form.getFieldValue('receiverName')) form.setFieldsValue({ receiverName: option.name })
    }

    return (
        <>
            {created && (
                <CreatedTicket
                    noun="collection"
                    id={created.id}
                    details={[
                        { label: 'Amount', value: `${groupThousands(created.values.amount)} ${created.values.currency}` },
                        { label: 'Channel', value: channelOptions.find((option) => option.value === created.values.method)?.label },
                        { label: 'Sender', value: created.values.senderName },
                        { label: 'Receiver account', value: created.values.receiverAccountNumber },
                    ]}
                    onUpdateStatus={() => navigate(paths.collection.update(created.id))}
                    onCreateAnother={() => setCreated(null)}
                />
            )}

            {/* Unmounted while the ticket shows; the form instance keeps its values, so "Create another" starts from the previous ones */}
            {!created && (
                <Form form={form} name="collection-create" layout="vertical" onFinish={onFinish}>
                    <div className="stack">
                        <div className="form-toolbar">
                            <Text type="secondary">Fields marked * are required.</Text>
                            <Button icon={<ExperimentOutlined />} onClick={() => fillEmpty(form, collectionSample())}>
                                Fill sample data
                            </Button>
                        </div>

                        <Card title="Sender Information">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Name"
                                        name="senderName"
                                        rules={[{ required: true, message: 'Please input Sender Name' }]}
                                        extra="Some sender names are held for compliance review. You can approve or reject those in Update Collection."
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Account Number" name="senderAccountNumber" rules={[{ required: true, message: 'Please input Sender Account Number' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="BIC/SWIFT" name="senderBankBIC" rules={[{ required: true, message: 'Please input BIC/SWIFT' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Card title="Receiver Information">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Account Number"
                                        name="receiverAccountNumber"
                                        rules={[{ required: true, message: 'Please input Receiver Account Number' }]}
                                        extra="The collection account (VA number) that receives the deposit"
                                    >
                                        <AutoComplete options={accountNumberOptions} filterOption={matchesInput} onSelect={onPickAccount} placeholder="Enter VA Number" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Name" name="receiverName" rules={[{ required: true, message: 'Please input Receiver Name' }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        <Card title="Deposit Information">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <AmountField label="Deposit Amount" currencyName="currency" />
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Payment Channel" name="method" rules={[{ required: true, message: 'Please input Payment Channel' }]}>
                                        <Radio.Group optionType="button" buttonStyle="solid" options={channelOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Payment Reference" name="customerReference">
                                        <Input placeholder="Optional" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {error && <Alert type="error" showIcon message={error} />}

                        <div className="form-actions">
                            <Button type="primary" size="large" htmlType="submit" loading={submitting}>
                                Create Collection
                            </Button>
                            <Button size="large" onClick={() => form.resetFields()}>
                                Reset
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default CollectionCreateForm
